import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHistory, useParams } from "react-router-dom";

import { IBateCadastralApi } from "./types";
import { formatDate, formatMonth, mensagemExecucaoEmAndamento, mensagemExecucaoFalhou, mensagemExecucaoSemAchados } from "./utils";
import api from "services/api";
import { useAlert } from "contexts/alert";

import { Box, Button, CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Modal, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ptBR } from '@mui/x-data-grid/locales';
import SearchIcon from '@mui/icons-material/Search';
import { cpfMask } from "utils/cpf";
import { Close } from "@mui/icons-material";

type Filters = {
  personName: string;
  personType: 'all' | 'employee' | 'dependent';
  inconsistencyType: string;
  /** `ORIGEM` compara a regra contra a operadora; `OPERADORA`, a nossa decisão contra ela. */
  tipoBate: string;
  classe: string;
}

const TIPO_BATE_LABEL: Record<string, string> = {
  ORIGEM: 'Cadastro (regra × operadora)',
  OPERADORA: 'Execução (Conducco × operadora)',
};

type Props = {
  exportWorkspaceActivityId: string,
  bateWorkspaceActivityId: string,
}

export function View({ exportWorkspaceActivityId, bateWorkspaceActivityId }: Props) {
  const { workspaceId, templateId } = useParams() as { workspaceId: string; templateId: string; };
  const history = useHistory();
  const { openAlert } = useAlert();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalExecuteOpen, setIsModalExecuteOpen] = useState(false);
  const [isModalExportOpen, setIsModalExportOpen] = useState(false);

  const [isSendingExecute, setIsSendingExecute] = useState(false);
  const [isSendingExport, setIsSendingExport] = useState(false);

  const [currentFilters, setCurrentFilters] = useState<Filters>({
    personName: '',
    personType: 'all',
    inconsistencyType: 'all',
    tipoBate: 'all',
    classe: 'all',
  });

  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["bate-cadastral", workspaceId],
    queryFn: () => api.get<{ data: IBateCadastralApi }>(`/v2/bate-cadastral/${bateWorkspaceActivityId}`),
    select: (data) => data.data.data,
    refetchInterval: 5000,
  });

  function handleFilter<K extends keyof Filters>(key: K, value: Filters[K]) {
    setCurrentFilters((current) => ({ ...current, [key]: value }));
  };

  /** As opções vêm do que a execução produziu, não de uma lista fixa de 20 achados. */
  const tiposDeInconsistencia = [...new Set((data?.people ?? []).map((p) => p.inconsistency))].sort();
  const tiposDeBate = [...new Set((data?.people ?? []).map((p) => p.tipoBate).filter(Boolean))] as string[];

  // Definição das colunas do DataGrid
  const columns: GridColDef[] = [
    { 
      field: 'name', 
      headerName: 'Nome', 
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => params.value || '-'
    },
    { 
      field: 'taxPayerId', 
      headerName: 'CPF Beneficiário', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? cpfMask(params.value as string) : '-'
    },
    { 
      field: 'taxPayerIdEmployee', 
      headerName: 'CPF Titular', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? cpfMask(params.value as string) : '-'
    },
    {
      field: 'beneficiaryType',
      headerName: 'Tipo do beneficiário',
      width: 160,
      renderCell: (params: GridRenderCellParams) =>
        params.value === 'employee' ? 'Titular' : 'Dependente'
    },
    {
      field: 'tipoBate',
      headerName: 'Comparação',
      width: 230,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? TIPO_BATE_LABEL[params.value as string] ?? params.value : '-'
    },
    {
      field: 'classe',
      headerName: 'Ação',
      width: 110,
      renderCell: (params: GridRenderCellParams) =>
        params.value === 'acionavel' ? 'Acionável' : params.value === 'registro' ? 'Registro' : '-'
    },
    {
      field: 'inconsistency',
      headerName: 'Tipo de inconsistência',
      width: 320,
      renderCell: (params: GridRenderCellParams) => params.value || '-'
    },
    { 
      field: 'valuePosicaoCadastral', 
      headerName: 'Dado na posição', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => params.value || '-'
    },
    {
      field: 'valueSystem',
      headerName: 'Dado no sistema',
      width: 150,
      renderCell: (params: GridRenderCellParams) => params.value || '-'
    }
  ];

  // Filtragem e transformação dos dados
  const busca = currentFilters.personName.toLowerCase();

  const filteredData = data ? data.people.filter(person =>
    (busca === '' ||
     person?.name?.toLowerCase()?.includes(busca) ||
     person?.taxPayerId?.toLowerCase()?.includes(busca)) &&
    (currentFilters.personType === 'all' || currentFilters.personType === person.beneficiaryType) &&
    (currentFilters.inconsistencyType === 'all' || currentFilters.inconsistencyType === person.inconsistency) &&
    (currentFilters.tipoBate === 'all' || currentFilters.tipoBate === person.tipoBate) &&
    (currentFilters.classe === 'all' || currentFilters.classe === person.classe)
  ) : [];

  // Transformação dos dados para o DataGrid
  const rows = filteredData.map((person, index) => ({
    id: `${person.taxPayerId}_${index}`,
    name: person.name,
    taxPayerId: person.taxPayerId,
    taxPayerIdEmployee: person.taxPayerIdEmployee,
    beneficiaryType: person.beneficiaryType,
    tipoBate: person.tipoBate,
    classe: person.classe,
    inconsistency: person.inconsistency,
    valuePosicaoCadastral: person.valuePosicaoCadastral,
    valueSystem: person.valueSystem,
  }));

  async function handleExecuteBate() {
    try {
      setIsSendingExecute(true);

      await api.post(`/v2/bate-cadastral/${bateWorkspaceActivityId}`);

      openAlert('Bate cadastral está sendo executado e já atualizará a tela', 'success');
    } catch (error) {
      openAlert('Houve algum error na execução', 'error');
    } finally {
      setIsSendingExecute(false);
      setIsModalExecuteOpen(false);
    }
  }

  async function handleExportBate() {
    try {
      setIsSendingExport(true);

      /** Com `await`: o sucesso só se anuncia depois de o backend gerar e enviar o arquivo. */
      await api.post(`/v2/bate-cadastral/${exportWorkspaceActivityId}/export`);

      openAlert('Arquivo exportado com sucesso!', 'success');
    } catch (error: any) {
      /** A mensagem do backend só em 4xx; um 5xx traz detalhe interno que não é para o cliente. */
      const status = error?.response?.status;
      const mensagemDoBackend = status >= 400 && status < 500 ? error?.response?.data?.data?.message : undefined;
      openAlert(mensagemDoBackend ?? 'Não foi possível exportar o arquivo', 'error');
    } finally {
      setIsSendingExport(false);
      setIsModalExportOpen(false);
    }
  }

  return (
    <>
      {!isFetched && isFetching && <div>Loading...</div>}
      {!isFetched && isError && <div>Error...</div>}
      {data && (
        <>
          <Modal open={isModalOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
            <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 800, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
              <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
                <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Atividades relacionadas</h6>
                <Close style={{"cursor":"pointer"}} onClick={() => setIsModalOpen(false)}/>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '34px', marginTop: '24px' }}>
                <span style={{ fontSize: '16px', lineHeight: '175%', fontWeight: '400', color: '#191C1A' }}>Para ter <strong>Bate cadastral</strong> atualizado verifique se as atividades abaixo estão atualizadas.</span>
              </div>
              <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
                <Table>
                  <TableRow style={{ height: "70px" }}>
                    <TableCell>
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400"}}>Nome</span>
                    </TableCell>
                    <TableCell>
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400" }}>Última atualização</span>
                    </TableCell>
                    <TableCell>
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400" }}>Execução programada</span>
                    </TableCell>
                    <TableCell>
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400" }}>Status</span>
                    </TableCell>
                  </TableRow>
                  {data.relatedActivities.map((activity) => (
                    <TableRow key={activity.name} style={{ height: "70px" }}>
                      <TableCell>{activity.name}</TableCell>
                      <TableCell>{!!activity?.latestExecution ? formatDate(activity.latestExecution) : "-"}</TableCell>
                      <TableCell>{!!activity?.scheduledExecution ? formatDate(activity.scheduledExecution) : "-"}</TableCell>
                      <TableCell>
                        {activity?.latestStatus === 'IDLE' && (
                          <div style={{ color: '#191C1A', backgroundColor: '#191C1A14', opacity: '0.8', padding: '0.25rem 0.75rem', borderRadius: '0.75rem', fontWeight: '400', fontSize: '12px', lineHeight: '16px', width: 'fit-content', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            <span>Aguardando</span>
                          </div>
                        )}
                        {activity?.latestStatus === 'PENDING' && (
                          <div style={{ color: '#EF6C00', backgroundColor: '#EF6C0014', opacity: '0.8', padding: '0.25rem 0.75rem', borderRadius: '0.75rem', fontWeight: '400', fontSize: '12px', lineHeight: '16px', width: 'fit-content', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            <span>Pendente</span>
                          </div>
                        )}
                        {activity?.latestStatus === 'SUCCESS' && (
                          <div style={{ color: '#2E7D32', backgroundColor: '#2E7D3214', opacity: '0.8', padding: '0.25rem 0.75rem', borderRadius: '0.75rem', fontWeight: '400', fontSize: '12px', lineHeight: '16px', width: 'fit-content', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            <span>Executado</span>
                          </div>
                        )}
                        {activity?.latestStatus === 'FAILED' && (
                          <div style={{ color: '#a30000', backgroundColor: '#ff7d7d3b', opacity: '0.8', padding: '0.25rem 0.75rem', borderRadius: '0.75rem', fontWeight: '400', fontSize: '12px', lineHeight: '16px', width: 'fit-content', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            <span>Falhou</span>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </Table>
              </TableContainer>
            </Box>
          </Modal>
          <Modal open={isModalExecuteOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
            <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 800, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
              <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
                <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Executar bate cadastral</h6>
                <Close style={{"cursor":"pointer"}} onClick={() => setIsModalExecuteOpen(false)}/>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '34px', marginTop: '24px' }}>
                <span style={{ fontSize: '16px', lineHeight: '175%', fontWeight: '400', color: '#191C1A' }}>Ao executar bate cadastral, será usado a última posição cadastral enviada para fazer a comparação, verifique se a data da ultima carga de posição cadastral está correta.</span>
              </div>
              <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
                <Table>
                  <TableRow style={{ height: "70px" }}>
                    <TableCell>
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400"}}>Nome</span>
                    </TableCell>
                    <TableCell>
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400" }}>Última atualização</span>
                    </TableCell>
                  </TableRow>
                  {data.relatedActivities.map((activity) => (
                    <TableRow key={activity.name} style={{ height: "70px" }}>
                      <TableCell>{activity.name}</TableCell>
                      <TableCell>{!!activity?.latestExecution ? formatDate(activity.latestExecution) : "-"}</TableCell>
                    </TableRow>
                  ))}
                </Table>
              </TableContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '34px', marginTop: '44px' }}>
                <span style={{ fontSize: '16px', lineHeight: '175%', fontWeight: '400', color: '#191C1A' }}>Caso houver alguma execução realizada anteriormente ela sera arquivada e pode ser visualizada no histórico.</span>
              </div>

              <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
                <div style={{"display":"flex","gap":"0.25rem"}}>
                  <Button
                    variant='outlined'
                    color='error'
                    style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    onClick={() => setIsModalExecuteOpen(false)}
                    disabled={isSendingExecute}
                  >
                    Cancelar
                  </Button>
      
                  <Button
                    variant='contained'
                    style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    onClick={handleExecuteBate}
                    disabled={isSendingExecute}
                  >
                    {!isSendingExecute ? "Executar bate cadastral" : <CircularProgress size="20px"/>}
                  </Button>
                </div>
              </div>
            </Box>
          </Modal>
          <Modal open={isModalExportOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
            <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 800, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
              <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
                <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Exportar arquivo</h6>
                <Close style={{"cursor":"pointer"}} onClick={() => setIsModalExportOpen(false)}/>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '34px', marginTop: '24px' }}>
                <span style={{ fontSize: '16px', lineHeight: '175%', fontWeight: '400', color: '#191C1A' }}>Se você deseja exportar as inconsistências, clique no botão <strong>"Exportar"</strong>. Caso queira verificar novamente, clique em <strong>"Verificar novamente"</strong></span>
              </div>
              <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
                <div style={{"display":"flex","gap":"0.25rem"}}>
                  <Button
                    variant='outlined'
                    color='error'
                    style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    onClick={() => setIsModalExportOpen(false)}
                    disabled={isSendingExport}
                  >
                    Verificar novamente
                  </Button>
      
                  <Button
                    variant='contained'
                    style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    onClick={handleExportBate}
                    disabled={isSendingExport}
                  >
                    {!isSendingExport ? "Exportar" : <CircularProgress size="20px"/>}
                  </Button>
                </div>
              </div>
            </Box>
          </Modal>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600,marginBottom: '24px'}}>Bate cadastral</h1>
  
            <div style={{"display":"flex","gap":"0.25rem", alignItems: "center"}}>
              <Button variant="outlined" style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "8px", height: "30px", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
                onClick={() => setIsModalOpen(true)}
              >
                Atividades relacionadas
              </Button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', gap: '50px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Executado em</p>
              <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: '600', color: '#6F6F78' }}>{formatDate(data.ultimaExecucao?.em ?? data.dates.executedAt)}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Referente a posição cadastral do mês</p>
              <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: '600', color: '#6F6F78' }}>{formatMonth(data.denominador.dateReference ?? data.dates.monthReference)}</span>
            </div>
            {/* O denominador: sem ele, zero achados é indistinguível de aprovação. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Comparado contra</p>
              <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: '600', color: data.denominador.linhasPosicaoCadastral === 0 ? '#D32F2F' : '#6F6F78' }}>
                {data.denominador.linhasPosicaoCadastral === null
                  ? 'sem contagem'
                  : data.denominador.linhasPosicaoCadastral === 0
                    ? 'nenhuma linha de posição carregada'
                    : `${data.denominador.linhasPosicaoCadastral} linhas de posição`}
              </span>
            </div>
          </div>

          {/**
            * Sem posição carregada o bate devolve zero achados, e zero parece aprovação. Este
            * aviso é o que separa "está tudo certo" de "não havia nada para comparar".
            */}
          {data.denominador.linhasPosicaoCadastral === 0 && (
            <div style={{ border: 'solid 1px #D32F2F', background: '#FDECEA', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px' }}>
              <span style={{ fontSize: '14px', color: '#611A15' }}>
                Não há posição cadastral carregada para este contrato. O resultado abaixo não
                significa que está tudo certo — significa que não havia nada com que comparar.
                Carregue a posição da operadora antes de interpretar este bate.
              </span>
            </div>
          )}

          {/**
            * A execução mais recente concluiu e não achou nada. Sem esta linha, zero achados não
            * se distingue de "o bate não rodou", e o cliente não sabe que funcionou. O verde fica
            * para quando houve contra o que comparar; sem contagem ou sem posição, o tom é neutro.
            */}
          {data.ultimaExecucao?.status === 'PENDING' && data.ultimaExecucao.iniciadaEm && (
            <div style={{ border: 'solid 1px #EF6C00', background: '#EF6C0014', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px' }}>
              <span style={{ fontSize: '14px', color: '#8A3E00' }}>
                {mensagemExecucaoEmAndamento(data.ultimaExecucao.iniciadaEm)}
              </span>
            </div>
          )}

          {/* O resultado abaixo não é da execução que falhou: a faixa diz de quando ele é. */}
          {data.ultimaExecucao?.status === 'FAILED' && (
            <div style={{ border: 'solid 1px #D32F2F', background: '#FDECEA', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px' }}>
              <span style={{ fontSize: '14px', color: '#611A15' }}>
                {mensagemExecucaoFalhou(data.ultimaExecucao.em)}
              </span>
            </div>
          )}

          {data.ultimaExecucao?.status === 'SUCCESS' && data.ultimaExecucao.totalAchados === 0 && data.ultimaExecucao.em && (
            <div style={{ border: `solid 1px ${(data.denominador.linhasPosicaoCadastral ?? 0) > 0 ? '#2E7D32' : '#BDBDBD'}`, background: (data.denominador.linhasPosicaoCadastral ?? 0) > 0 ? '#2E7D3214' : '#F5F5F5', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px' }}>
              <span style={{ fontSize: '14px', color: (data.denominador.linhasPosicaoCadastral ?? 0) > 0 ? '#1B5E20' : '#424242' }}>
                {mensagemExecucaoSemAchados(data.ultimaExecucao.em)}
              </span>
            </div>
          )}

          <div style={{"display":"flex","gap":"1rem"}}>
            {[
              { label: 'Total de inconsistências', value: data.metrics.countInconsistencies },
              { label: 'Acionáveis', value: data.metrics.countAcionaveis },
              { label: 'Titulares', value: data.metrics.countEmployees },
              { label: 'Dependentes', value: data.metrics.countDependents },
            ].map(({ label, value }) => (
              <div key={label} style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column', marginBottom: '30px' }}>
                <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>{label}</p>
                <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Os controles que faltavam: o estado de filtro existia e nada o alimentava. */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Nome ou CPF"
              value={currentFilters.personName}
              onChange={(event) => handleFilter('personName', event.target.value)}
              sx={{ minWidth: 240, backgroundColor: 'white' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 260, backgroundColor: 'white' }}>
              <InputLabel id="filtro-tipo-bate">Comparação</InputLabel>
              <Select
                labelId="filtro-tipo-bate"
                label="Comparação"
                value={currentFilters.tipoBate}
                onChange={(event) => handleFilter('tipoBate', event.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                {tiposDeBate.map((tipo) => (
                  <MenuItem key={tipo} value={tipo}>{TIPO_BATE_LABEL[tipo] ?? tipo}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150, backgroundColor: 'white' }}>
              <InputLabel id="filtro-classe">Ação</InputLabel>
              <Select
                labelId="filtro-classe"
                label="Ação"
                value={currentFilters.classe}
                onChange={(event) => handleFilter('classe', event.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="acionavel">Acionável</MenuItem>
                <MenuItem value="registro">Registro</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150, backgroundColor: 'white' }}>
              <InputLabel id="filtro-tipo-pessoa">Beneficiário</InputLabel>
              <Select
                labelId="filtro-tipo-pessoa"
                label="Beneficiário"
                value={currentFilters.personType}
                onChange={(event) => handleFilter('personType', event.target.value as Filters['personType'])}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="employee">Titular</MenuItem>
                <MenuItem value="dependent">Dependente</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 320, backgroundColor: 'white' }}>
              <InputLabel id="filtro-inconsistencia">Tipo de inconsistência</InputLabel>
              <Select
                labelId="filtro-inconsistencia"
                label="Tipo de inconsistência"
                value={currentFilters.inconsistencyType}
                onChange={(event) => handleFilter('inconsistencyType', event.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                {tiposDeInconsistencia.map((tipo) => (
                  <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div style={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              style={{ backgroundColor: "white" }}
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
              sx={{
                "& .MuiDataGrid-root": {
                  border: 'none',
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: '#f5f5f5',
                  borderBottom: 'none',
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: '#ffffff',
                  "&::-webkit-scrollbar": {
                    width: "12px",
                    height: "12px"
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#C1C1C1",
                    borderRadius: "8px"
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#F5F5F5"
                  }
                },
              }}
            />
          </div>
          
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end',"paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"24px" }}>
            {/* Leva para a grade de retificação, uma linha por beneficiário. */}
            <Button
              variant='text'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => history.push(`/v2/workspaces/${workspaceId}/templates/${templateId}/bate-cadastral/${bateWorkspaceActivityId}/retificar`)}
            >
              Exceções
            </Button>
            <Button
              variant='text'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setIsModalExportOpen(true)}
            >
              Exportar
            </Button>
            <Button
              variant='contained'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setIsModalExecuteOpen(true)}
            >
              Executar bate cadastral
            </Button>
          </div>
        </>
      )}
    </>
  );
}
